/**
 * useHouseSettings hook
 * Fetches houses + upcoming capacity matrix in parallel.
 * Provides mutations for CRUD on houses and bulk matrix save.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys, invalidateQueries } from '../lib/queryClient';
import { useCourseTime } from '../contexts/CourseTimeContext';

// Fetch all houses (active and inactive)
function useHousesList() {
  return useQuery({
    queryKey: queryKeys.houses.list({}),
    queryFn: () => api.houses.getAll({}),
    select: (data) => data.results ?? data,
    staleTime: 5 * 60 * 1000,
  });
}

// Fetch capacity matrix — respects the global course-time filter
function useCapacityMatrix() {
  const { showAllTime } = useCourseTime();
  return useQuery({
    queryKey: [...queryKeys.houses.capacityMatrix(), showAllTime ? 'all' : 'upcoming'],
    queryFn: () => api.houseCapacities.capacityMatrix(showAllTime),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Primary hook for the Houses Settings page.
 *
 * Returns:
 *   houses    – array of active House objects
 *   courses   – array of upcoming Course objects (from matrix)
 *   matrix    – { [houseUuid]: { [courseUuid]: { capacity, gender_designation, capacity_uuid } | null } }
 *   isLoading – true while either query is loading
 *   error     – first error encountered, or null
 *   saveMatrix(rows)         – bulk_update all capacity cells
 *   createHouse(data)        – POST new house
 *   updateHouse(uuid, data)  – PATCH existing house
 *   deleteHouse(uuid)        – DELETE house
 */
export function useHouseSettings() {
  const housesQuery = useHousesList();
  const matrixQuery = useCapacityMatrix();

  // --- Mutations ---

  const saveMatrixMutation = useMutation({
    mutationFn: (capacities) => api.houseCapacities.bulkUpdate(capacities),
    onSuccess: () => {
      invalidateQueries.houseMatrix();
    },
  });

  const createHouseMutation = useMutation({
    mutationFn: (data) => api.houses.create(data),
    onSuccess: () => {
      invalidateQueries.houses();
      invalidateQueries.houseMatrix();
    },
  });

  const updateHouseMutation = useMutation({
    mutationFn: ({ uuid, ...data }) => api.houses.update(uuid, data),
    onSuccess: () => {
      invalidateQueries.houses();
      invalidateQueries.houseMatrix();
    },
  });

  const deleteHouseMutation = useMutation({
    mutationFn: (uuid) => api.houses.delete(uuid),
    onSuccess: () => {
      invalidateQueries.houses();
      invalidateQueries.houseMatrix();
    },
  });

  return {
    // Data
    houses: housesQuery.data ?? [],
    courses: matrixQuery.data?.courses ?? [],
    matrix: matrixQuery.data?.matrix ?? {},
    enrollment: matrixQuery.data?.enrollment ?? {},

    // Loading / error state
    isLoading: housesQuery.isLoading || matrixQuery.isLoading,
    error: housesQuery.error || matrixQuery.error || null,

    // Mutations
    saveMatrix: saveMatrixMutation.mutateAsync,
    isSavingMatrix: saveMatrixMutation.isPending,

    createHouse: createHouseMutation.mutateAsync,
    isCreating: createHouseMutation.isPending,

    updateHouse: updateHouseMutation.mutateAsync,
    isUpdating: updateHouseMutation.isPending,

    deleteHouse: deleteHouseMutation.mutateAsync,
    isDeleting: deleteHouseMutation.isPending,
  };
}

export default useHouseSettings;
